import { env } from '@/env';
import { getAppType, getNativeAppVersion } from '@/shared/bridge';
import { collectEvent, startCollect } from './collect';

/**
 * GA4 수집 진입점. **브라우저·Android·iOS 를 하나의 경로로** 잰다 — gtag.js 를 쓰지 않고 전 표면이
 * `/g/collect` 직접 전송(`collect.ts`)을 탄다.
 *
 * 통일한 이유: iOS WebView(`app://`)에서 **gtag.js 가 전송을 아예 하지 않는다**(근거·실측은
 * `collect.ts` 주석). 거기만 직접 전송으로 두면 표면마다 세션·이벤트 정의가 갈려 "웹 1명 = 앱 1명"
 * 전제가 깨지고, 웹↔앱 비교 자체가 무의미해진다.
 *
 * 그래서 **모든 이벤트는 우리가 명시적으로 보낸다.** 자동으로 발생하는 이벤트는 없다
 * (세션 시작·첫 방문은 우리가 세운 플래그로 GA4 가 파생시킨다 — `collect.ts` 참조).
 *
 * `env.gaMeasurementId` 가 없으면 **완전 no-op**.
 */
/** analytics client id 영속 키. 토큰·terminalId 와 **의도적으로 분리** — `getOrCreateClientId` 주석 참조. */
const CLIENT_ID_KEY = 'heviton.ga_client_id';

let started = false;

/**
 * 네이티브 **바이너리** 버전 — 웹 번들 버전(`app_version`)과 **다른 축**이라 따로 싣는다.
 *
 * OTA 는 web 만 갱신하므로 **한 바이너리 위에 여러 웹 버전이 올라간다.** `app_version` 만 보면
 * "OTA 는 갔는데 바이너리가 구버전" 인 조합을 가려낼 수 없어, 앱 이슈를 추적할 때 정작 필요한 축이
 * 빠진다(스토어 빌드가 있어야만 켜지는 기능 — CSP 확장 같은 것 — 의 도달률이 대표적).
 *
 * 브라우저에선 `getNativeAppVersion()` 이 `null` 이라 **파라미터 자체를 싣지 않는다.** 빈 문자열로
 * 채우면 GA4 리포트에 의미 없는 값이 한 줄 생긴다.
 *
 * 필요해지면 같은 방식으로 `getNativeBuildNumber()`(정확한 바이너리 특정)·`getNativeChannel()`
 * (OTA 채널)도 실을 수 있다 — 다만 파라미터 하나당 GA4 맞춤 측정기준 하나를 써야 한다.
 */
function nativeVersionParams(): Record<string, unknown> {
  const nativeVersion = getNativeAppVersion();
  return nativeVersion ? { native_version: nativeVersion } : {};
}

/**
 * **사용자 식별(client id)은 전 플랫폼 우리 UUID 로 통일**한다.
 *
 * 쿠키(gtag 가 쓰던 `_ga`)를 안 쓰는 이유:
 *   - **iOS WebView** 는 `app://` origin 이라 **쿠키가 저장되지 않는다**. 실측: `document.cookie='p=1'`
 *     은 **예외를 던지지 않는데** 곧바로 읽으면 빈 문자열이다 — 조용히 버려진다.
 *   - **Safari ITP** 는 JS 로 쓴 쿠키를 **7일**로 잘라낸다. 발전소주는 매일 오는 사용자가 아니라
 *     (주·월 단위 조회), 쿠키에 맡기면 재방문자가 계속 신규로 잡혀 리텐션이 무의미해진다.
 *   - localStorage 는 두 제약을 다 받지 않고 **전 표면에서 동일하게 동작**한다 — 그래야 웹·앱
 *     사용자 수를 같은 기준으로 셀 수 있다.
 *
 * 세션(`sid`)도 같은 저장소에 함께 둔다 — 관리 주체는 `collect.ts` 다.
 *
 * `getTerminalId()` 를 재사용하지 않는 이유: 그건 BE 세션 신원(`session.app.terminalId`)이다. 인증에
 * 쓰는 식별자를 제3자에게 넘기면 우리 서버 데이터와 GA 를 잇는 조인 키를 만들어 주는 셈이라, 분석
 * 전용 UUID 를 따로 발급한다. 로그인 계정(`user_id`)도 **보내지 않는다** — 익명 집계가 현재 정책이다.
 */
function getOrCreateClientId(): string {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const generated = randomClientId();
    localStorage.setItem(CLIENT_ID_KEY, generated);
    return generated;
  } catch {
    // localStorage 차단 환경 — 그 실행 동안만 일관된 값. 분석 정확도만 떨어지고 동작엔 무해.
    return randomClientId();
  }
}

/**
 * client id 생성. **암호학적 강도는 요구사항이 아니다** — 분석 식별자라 예측돼도 할 수 있는 게 없다
 * (인증·인가에 쓰이지 않는다). 요구사항은 오직 **충돌 회피**와 **길이 안정성**이다.
 *
 * 3단 폴백인 이유:
 *   1. `crypto.randomUUID` — 표준 경로. 명세상 **secure context 전용**이다. iOS WebView 의 `app://`
 *      origin 은 커스텀 scheme 이라 의심했지만 **실측 결과 정상 동작**했다(시뮬레이터 + Safari
 *      웹 인스펙터). 즉 실사용 경로는 사실상 여기서 끝난다.
 *   2. `crypto.getRandomValues` — **secure context 제약이 없다.** 혹시 1번이 막히는 환경(비 secure
 *      context 로 뜨는 WebView·구형 브라우저)이 나오더라도 온전한 난수를 준다.
 *   3. `Math.random` — `crypto` 자체가 없는 환경의 최후 수단. `toString(36)` 은 **자릿수가 보장되지
 *      않으므로**(`0.5` → `'0.i'` → 1글자) 패딩으로 길이를 맞춘다. 타임스탬프를 앞에 둬 같은
 *      밀리초가 아니면 충돌 자체가 불가능하게 만든다(GA 기본 client id 도 같은 `난수.시각` 형식).
 */
function randomClientId(): string {
  const webCrypto = typeof crypto !== 'undefined' ? crypto : undefined;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  const rand = Math.random().toString(36).slice(2).padEnd(10, '0').slice(0, 10);
  return `${Date.now()}.${rand}`;
}

/**
 * 개발자 로컬(`*.prod.local`)이 운영 property 를 두드릴 때 이벤트를 **내부 트래픽으로 표시**한다.
 *
 * 로컬 검증은 운영과 같은 스트림에 실제로 쏴 봐야 의미가 있는데, 그 이벤트가 실사용 지표에 섞이면
 * 안 된다. `traffic_type` 은 GA4 콘솔의 "Internal Traffic" 데이터 필터가 걸러낼 표식이고
 * (⚠️ 그 필터를 **활성화**해야 실제로 빠진다 — 기본값은 `테스트`라 표시만 된다),
 * `debug_mode` 는 같은 이벤트를 DebugView 로 보내 눈으로 확인하게 해준다.
 */
function internalTrafficParams(): Record<string, unknown> {
  if (!env.gaInternalTraffic) return {};
  return { traffic_type: 'internal', debug_mode: true };
}

/** GA4 초기화. env 미설정이면 no-op, 두 번 불러도 1회만 수행. 배선은 `app/runtime/analytics.ts`. */
export function startAnalytics(): void {
  const measurementId = env.gaMeasurementId;
  if (!measurementId || started) return;
  started = true;

  startCollect({
    measurementId,
    clientId: getOrCreateClientId(),
    // 모든 히트에 실리는 공통 파라미터. 리포트에서 쓰려면 GA4 콘솔에 맞춤 측정기준(이벤트 범위) 등록 필요.
    params: {
      platform: getAppType(),
      app_version: env.appVersion,
      ...nativeVersionParams(),
      ...internalTrafficParams(),
    },
  });
}

/**
 * 라우트 1건 = page_view 1건. 호출처는 `app/runtime/analytics.ts` 하나.
 *
 * `page_location` 을 **배포 도메인 기준으로 덮어쓴다** — WebView 는 origin 이
 * `app://app.heviton.local`(iOS) / `https://app.heviton.local`(Android) 이라 그대로 두면 앱 트래픽이
 * 딴 호스트로 갈려 웹과 비교가 안 된다. 같은 URL 축으로 통일하는 게 "웹·앱 동일 조건"의 실체다.
 *
 * 쿼리스트링은 **의도적으로 뺀다** — 필터 상태(날짜·발전소 id)가 그대로 실려 URL 카디널리티가
 * 폭발하고, 파라미터에 개인 식별 정보가 섞여 들어갈 여지도 없앤다.
 */
export function trackPageView(pathname: string): void {
  if (!started) return;
  collectEvent('page_view', { page_location: `${env.deployBaseUrl}${pathname}` });
}

/**
 * 커스텀 이벤트 전송. env 미설정·초기화 전이면 조용히 무시된다.
 *
 * ⚠️ `params` 에 **개인 식별 정보를 넣지 말 것** (이메일·이름·전화번호·주소 등). GA4 정책 위반이고
 * 적발 시 property 가 삭제될 수 있다. 내부 id 를 실어야 하면 용도를 주석으로 남길 것.
 */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!started) return;
  collectEvent(name, params);
}
